/**
 * LoginPage - Page Component
 * Renderiza el login RBAC por paso usando componentes UI existentes.
 * Toda la lógica vive en el hook useLogin (Clean Architecture).
 * Flujo: credenciales → (opcional) new-password / OTP (email·sms·app) / mfa-setup → perfil.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { OtpInput } from '@/components/auth/OtpInput';
import { PasswordWithConfirm } from '@/components/auth/PasswordWithConfirm';
import { MfaSetupScreen } from '@/components/auth/MfaSetupScreen';
import { useLogin } from '@/hooks/useLogin';
import { useOtpCooldown } from '@/hooks/useOtpCooldown';
import { maskEmail, maskPhone, formatTime } from '@/lib/otp';
import { isEmail } from '@/lib/identifier';
import { ROUTES } from '@/constants';

export function LoginPage() {
  const {
    step, otpLength, canResend, error, loading,
    submitCredentials, submitChallenge, resend, backToCredentials,
  } = useLogin();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');       // contraseña de credenciales (login)
  const [newPassword, setNewPassword] = useState(''); // contraseña nueva (NEW_PASSWORD_REQUIRED) — estado separado
  const [answer, setAnswer] = useState('');
  const [confirm, setConfirm] = useState('');
  const [valid, setValid] = useState(false);

  // Hook de cooldown: SIEMPRE se llama (regla de hooks), se usa solo en el paso OTP.
  const { secondsLeft, canResend: cooldownCanResend, markSent } = useOtpCooldown('login');

  // Al entrar al paso OTP con reenvío disponible (email/sms) arrancamos el cooldown.
  useEffect(() => {
    if (step === 'otp' && canResend) markSent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, canResend]);

  const handleCredentials = (e: FormEvent) => {
    e.preventDefault();
    void submitCredentials(identifier.trim(), password).catch(() => {
      /* el error ya se refleja vía `error` del hook */
    });
  };

  const handleNewPassword = (e: FormEvent) => {
    e.preventDefault();
    void submitChallenge(newPassword);
  };

  const handleOtp = (e: FormEvent) => {
    e.preventDefault();
    void submitChallenge(answer);
  };

  const handleResend = () => {
    void resend().then(() => markSent());
  };

  // mfa-setup ocupa toda la pantalla (no usa el card).
  if (step === 'mfa-setup') {
    return <MfaSetupScreen onLogout={backToCredentials} />;
  }

  const errorBlock = error && (
    <div
      className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      role="alert"
    >
      {error}
    </div>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Fiado Credit
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {step === 'credentials' && 'Inicia sesion para acceder al panel'}
            {step === 'new-password' && 'Define tu nueva contraseña'}
            {step === 'otp' && 'Ingresa el código de verificación'}
          </p>
        </div>

        {step === 'credentials' && (
          /* ── Paso 1: credenciales ── */
          <form
            onSubmit={handleCredentials}
            className="space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm"
            noValidate
          >
            {errorBlock}

            <Input
              label="Usuario"
              type="text"
              name="username"
              placeholder="Ingresa tu usuario"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoComplete="username"
              disabled={loading}
            />

            <Input
              label="Contrasena"
              type="password"
              name="password"
              placeholder="Ingresa tu contrasena"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={loading}
            />

            <Button type="submit" fullWidth isLoading={loading} disabled={loading}>
              Iniciar sesion
            </Button>

            <Link
              to={ROUTES.FORGOT_PASSWORD}
              className="mt-4 block text-center text-sm text-muted-foreground underline"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </form>
        )}

        {step === 'new-password' && (
          /* ── Paso 2a: nueva contraseña requerida ── */
          <form
            onSubmit={handleNewPassword}
            className="space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm"
            noValidate
          >
            {errorBlock}

            <PasswordWithConfirm
              password={newPassword}
              confirm={confirm}
              onPassword={setNewPassword}
              onConfirm={setConfirm}
              onValidChange={setValid}
              disabled={loading}
            />

            <Button type="submit" fullWidth isLoading={loading} disabled={loading || !valid}>
              Cambiar contraseña
            </Button>
          </form>
        )}

        {step === 'otp' && (
          /* ── Paso 2b: OTP (email · sms · app autenticadora) ── */
          <form
            onSubmit={handleOtp}
            className="space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm"
            noValidate
          >
            {errorBlock}

            <p className="text-sm text-muted-foreground">
              {canResend
                ? `Te enviamos un código a ${isEmail(identifier) ? maskEmail(identifier) : maskPhone(identifier)}`
                : 'Ingresá el código de tu app de autenticación'}
            </p>

            <OtpInput
              value={answer}
              onChange={setAnswer}
              maxLength={otpLength}
              onSubmit={() => submitChallenge(answer)}
              disabled={loading}
              label="Código de verificación"
            />

            <Button
              type="submit"
              fullWidth
              isLoading={loading}
              disabled={loading || answer.length !== otpLength}
            >
              Verificar
            </Button>

            {canResend && (
              <div className="text-center text-sm text-muted-foreground">
                {cooldownCanResend ? (
                  <button
                    type="button"
                    onClick={handleResend}
                    className="text-primary hover:underline"
                  >
                    Reenviar código
                  </button>
                ) : (
                  <span>Puedes reenviar en {formatTime(secondsLeft)}</span>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
